// Shared cover-letter → clean one-page PDF generator (a "white page" download),
// used by both the Cover Letter page and the browser-extension sidebar so output is identical.
export async function downloadCoverLetterPdf(text, { companyName = "", jobTitle = "" } = {}) {
  if (!text) return;
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();   // 612
  const pageH = doc.internal.pageSize.getHeight();  // 792
  const M = 72; // 1-inch margins
  const usableW = pageW - M * 2;
  const fontSize = 11;
  const lineH = fontSize * 1.3;

  const allLines = text.split("\n");
  let recipientLines = [];
  let subjectLine = "";
  let salutation = "";
  let bodyParagraphs = [];
  let closingLine = "";
  let signatureName = "";
  let contactLines = [];

  let phase = "header";
  let currentPara = [];

  for (const raw of allLines) {
    const line = raw.trim();
    if (phase === "header" && /(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}/.test(line)) {
      continue;
    }
    if (!subjectLine && /^(?:Re:|Subject:|RE:)/i.test(line)) { subjectLine = line; phase = "body"; continue; }
    if (!salutation && /^(?:Dear|Hello|Hi)\s/i.test(line)) { salutation = line; phase = "body"; continue; }
    if (phase === "body" && /^(?:Sincerely|Best regards|Regards|Warm regards|Kind regards)\s*,?\s*$/i.test(line)) {
      if (currentPara.length > 0) { bodyParagraphs.push(currentPara.join(" ")); currentPara = []; }
      closingLine = line.replace(/,?\s*$/, ","); phase = "closing"; continue;
    }
    if (phase === "closing") {
      if (!line) continue;
      if (!signatureName && /^[A-Z][a-z]+/.test(line) && line.length < 50 && !line.includes("@") && !/\d{3}/.test(line) && !line.includes("linkedin")) {
        signatureName = line;
      } else if (line) { contactLines.push(line); }
      continue;
    }
    if (phase === "header" && line && !salutation && !subjectLine) { recipientLines.push(line); continue; }
    if (phase === "body") {
      if (!line) { if (currentPara.length > 0) { bodyParagraphs.push(currentPara.join(" ")); currentPara = []; } }
      else { currentPara.push(line); }
    }
  }
  if (currentPara.length > 0) bodyParagraphs.push(currentPara.join(" "));

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  if (!salutation) salutation = "Dear Hiring Manager,";
  if (!closingLine) closingLine = "Sincerely,";

  const availableH = pageH - M * 2;
  const calcHeight = (fs, lh) => {
    doc.setFontSize(fs);
    let h = 0;
    h += lh * 2;
    h += recipientLines.length * lh;
    if (recipientLines.length > 0) h += lh * 0.5;
    if (subjectLine) h += lh * 1.5;
    h += lh * 1.5;
    for (let i = 0; i < bodyParagraphs.length; i++) {
      const lines = doc.splitTextToSize(bodyParagraphs[i], usableW);
      h += lines.length * lh;
      if (i < bodyParagraphs.length - 1) h += lh * 0.6;
    }
    h += lh * 0.3;
    const closingWrapped = doc.splitTextToSize(closingLine, usableW);
    h += closingWrapped.length * lh;
    h += lh * 0.5;
    if (signatureName) h += lh;
    h += contactLines.length * lh;
    return h;
  };

  let finalFontSize = fontSize;
  let finalLineH = lineH;
  let totalH = calcHeight(finalFontSize, finalLineH);
  if (totalH > availableH) { finalLineH = finalFontSize * 1.2; totalH = calcHeight(finalFontSize, finalLineH); }
  if (totalH > availableH) { finalLineH = finalFontSize * 1.15; totalH = calcHeight(finalFontSize, finalLineH); }
  if (totalH > availableH) { finalFontSize = 10.5; finalLineH = finalFontSize * 1.2; totalH = calcHeight(finalFontSize, finalLineH); }
  if (totalH > availableH) { finalFontSize = 10; finalLineH = finalFontSize * 1.15; totalH = calcHeight(finalFontSize, finalLineH); }

  doc.setFont("times", "normal");
  doc.setFontSize(finalFontSize);
  doc.setTextColor(0, 0, 0);
  let y = M;

  const writeLine = (txt) => {
    const wrapped = doc.splitTextToSize(txt, usableW);
    for (const wl of wrapped) { doc.text(wl, M, y); y += finalLineH; }
  };
  const writeParagraph = (paragraph) => {
    const wrapped = doc.splitTextToSize(paragraph, usableW);
    for (const wl of wrapped) writeLine(wl);
  };

  writeLine(dateStr);
  y += finalLineH;
  for (const rl of recipientLines) writeLine(rl);
  if (recipientLines.length > 0) y += finalLineH * 0.5;
  if (subjectLine) { writeLine(subjectLine); y += finalLineH * 0.5; }
  writeLine(salutation);
  y += finalLineH * 0.5;
  for (let i = 0; i < bodyParagraphs.length; i++) {
    writeParagraph(bodyParagraphs[i]);
    if (i < bodyParagraphs.length - 1) y += finalLineH * 0.6;
  }
  y += finalLineH * 0.3;
  writeLine(closingLine);
  y += finalLineH * 0.5;
  if (signatureName) writeLine(signatureName);
  for (const cl of contactLines) writeLine(cl);

  doc.save(`Cover_Letter_${companyName || "Draft"}_${jobTitle || "Role"}.pdf`);
}
